'use client';

import { createContext, useContext } from 'react';
import { type components } from '~/fecom-api/schema';
import { trpc } from '~/trpc/client';

type FavouritesContextModel = {
  isFavouritesLoading: boolean;
  isMyListsLoading: boolean;
  favourites: string[];
  myLists: components['schemas']['MyList'][];
  addToFavourites: (catalogueRef: string) => void;
  removeFromFavourites: (catalogueRef: string) => void;
  addToMyList: (params: {
    catalogueRef: string;
    myListId: number;
    newListName?: string | null;
    isNewList: boolean;
  }) => void;
  deleteFromMyList: (params: {
    catalogueRef: string;
    myListId: number;
  }) => void;
  isInProductsILove: (params: { catalogueRef: string }) => boolean;
  isInMyList: (params: { catalogueRef: string; myListId: number }) => boolean;
  isInAnyList: (params: { catalogueRef: string }) => boolean;
};

const FavouritesContext = createContext<FavouritesContextModel>({
  isFavouritesLoading: false,
  isMyListsLoading: false,
  favourites: [],
  myLists: [],
  addToFavourites: () => {
    throw new Error('addToFavourites not implemented');
  },
  removeFromFavourites: () => {
    throw new Error('removeFromFavourites not implemented');
  },
  addToMyList: () => {
    throw new Error('addToMyList function is not implemented');
  },
  deleteFromMyList: () => {
    throw new Error('deleteFromMyList function is not implemented');
  },
  isInProductsILove: () => {
    throw new Error('isFavourite function is not implemented');
  },
  isInMyList: () => {
    throw new Error('isInMyList function is not implemented');
  },
  isInAnyList: () => {
    throw new Error('isInAnyList function is not implemented');
  },
});

type FavouritesProviderProps = {
  isAuthed: boolean;
  children: React.ReactNode;
};

export function FavouritesProvider({
  isAuthed,
  children,
}: FavouritesProviderProps) {
  const { data: favourites, isLoading: isFavouritesLoading } =
    trpc.productsILoveRouter.getProductsILove.useQuery();

  const { data: myLists, isLoading: isMyListsLoading } =
    trpc.myLists.getUserListsWithProducts.useQuery(undefined, {
      enabled: isAuthed,
    });

  const utils = trpc.useUtils();

  const addToFavourites = (catalogueRef: string) => {
    utils.productsILoveRouter.getProductsILove.setData(undefined, (prev) => {
      if (!prev) return { productsILove: [catalogueRef] };
      if (prev.productsILove.includes(catalogueRef)) return prev; // avoid duplicates
      return {
        productsILove: [...prev.productsILove, catalogueRef],
      };
    });
  };

  const removeFromFavourites = (catalogueRef: string) => {
    utils.productsILoveRouter.getProductsILove.setData(undefined, (prev) => {
      if (!prev) return { productsILove: [] };
      return {
        productsILove: prev.productsILove.filter((fav) => fav !== catalogueRef),
      };
    });
  };

  const addToMyList = ({
    catalogueRef,
    myListId,
    newListName,
    isNewList,
  }: {
    catalogueRef: string;
    myListId: number;
    newListName?: string | null;
    isNewList: boolean;
  }) => {
    utils.myLists.getUserListsWithProducts.setData(undefined, (prev) => {
      const newProduct = {
        catalogueRef,
        myListItemId: Math.floor(Math.random() * 100000), // Fake ID
        quantity: 1,
      };

      if (!prev) {
        // No cached data yet, create initial structure with the new list and product
        return {
          myLists: [
            {
              listId: myListId,
              listName: newListName ?? 'New List',
              products: [newProduct],
            },
          ],
        };
      }

      if (isNewList && newListName) {
        return {
          myLists: [
            ...prev.myLists,
            {
              listId: myListId,
              listName: newListName,
              products: [newProduct],
            },
          ],
        };
      }

      return {
        myLists: prev.myLists.map((list) =>
          list.listId === myListId
            ? {
                ...list,
                products: [...(list.products ?? []), newProduct],
              }
            : list,
        ),
      };
    });
  };

  const deleteFromMyList = ({
    catalogueRef,
    myListId,
  }: {
    catalogueRef: string;
    myListId: number;
  }) => {
    utils.myLists.getUserListsWithProducts.setData(undefined, (prev) => {
      if (!prev) return prev;

      return {
        myLists: prev.myLists.map((list) =>
          list.listId === myListId
            ? {
                ...list,
                products: list.products.filter(
                  (p) => p.catalogueRef !== catalogueRef,
                ),
              }
            : list,
        ),
      };
    });
  };

  return (
    <FavouritesContext.Provider
      value={{
        isFavouritesLoading,
        isMyListsLoading,
        favourites: favourites?.productsILove ?? [],
        myLists: myLists?.myLists ?? [],
        addToFavourites,
        removeFromFavourites,
        addToMyList,
        deleteFromMyList,
        isInProductsILove: (params) => {
          return (
            favourites?.productsILove.includes(params.catalogueRef) ?? false
          );
        },
        isInMyList: (params) => {
          return (
            myLists?.myLists.some(
              (list) =>
                list.listId === params.myListId &&
                list.products.some(
                  (p) => p.catalogueRef === params.catalogueRef,
                ),
            ) ?? false
          );
        },
        isInAnyList: (params) => {
          return (
            myLists?.myLists.some((list) =>
              list.products.some((p) => p.catalogueRef === params.catalogueRef),
            ) ?? false
          );
        },
      }}
    >
      {children}
    </FavouritesContext.Provider>
  );
}

export const useFavourites = () => {
  const context = useContext(FavouritesContext);
  if (!context) {
    throw new Error('useFavourites must be used within a FavouritesProvider');
  }
  return context;
};

export { FavouritesContext };
